import React from "react";
import { useTranslation } from "react-i18next";
import { StandardRatesHistoryColumns } from "us.collection.admin/components/StandardRates/Constants";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import {
  $TableTree,
  $Skeleton,
  ITableTreeColumns,
  $DateLabel,
  $Tooltip,
} from "us.common/components";
import { HistoryTableActionFilters } from "us.collection.admin/constants";
import { COLUMNS } from "./Constants";

/**
 * @description - Standard rate history table component for show all standard rate history data.
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3479208007/View+Standard+Rate+UI+Implementation
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 03/08/2024
 */
const HistoryTable: React.FC<PropsFromRedux> = (props) => {
  const { ratesHistory } = props;
  const { t } = useTranslation();

  /**
   * @function
   * @description Get table columns list
   * @returns {ITableTreeColumns} - TableTree columns
   */
  const getColumns = (): ITableTreeColumns => {
    const columns: ITableTreeColumns = [];

    COLUMNS.map(({ key, title, width }: any) => {
      let column: any = {
        key: key,
        dataIndex: key,
        title: title && t(title),
        width,
      };
      if (
        [
          StandardRatesHistoryColumns.RATE_ID,
          StandardRatesHistoryColumns.RATE,
        ].includes(key)
      ) {
        column = {
          ...column,
          ellipsis: { showTitle: false },
          customSorter: (a: any, b: any) => a - b,
          customFilter: true,
        };
      } else if (
        [
          StandardRatesHistoryColumns.MODIFIED_BY,
          StandardRatesHistoryColumns.COMMENT,
        ].includes(key)
      ) {
        column = {
          ...column,
          ellipsis: { showTitle: false },
          customSorter: (a: any, b: any) => a?.localeCompare(b),
          customFilter: true,
          customRenderChild: (text: any) => {
            return <$Tooltip title={text}>{text}</$Tooltip>;
          },
        };
      } else if (
        [
          StandardRatesHistoryColumns.EFFECTIVE_DATE,
          StandardRatesHistoryColumns.MODIFIED_DATE,
        ].includes(key)
      ) {
        column = {
          ...column,
          ellipsis: { showTitle: false },
          customSorter: (a: any, b: any) => a?.localeCompare(b),
          customFilter: "date",
          customRenderChild: (text: any) => {
            return <$DateLabel value={text} />;
          },
        };
      } else if ([StandardRatesHistoryColumns.ACTION].includes(key)) {
        column = {
          ...column,
          ellipsis: { showTitle: false },
          customSorter: (a: any, b: any) => a?.localeCompare(b),
          filters: HistoryTableActionFilters,
          filterSearch: true,
          onFilter: (value: string, { action }: any) =>
            action?.trim()?.toLowerCase() == value,
        };
      } else {
        // configure other columns
        column = {
          ...column,
          ellipsis: { showTitle: false },
          customFilter: true,
          customSorter: (a: any, b: any) => a?.localeCompare(b),
          customRenderChild: (text: any, record: any) => (
            <span>{record[key]}</span>
          ),
        };
      }
      // add configured column data object to the columns array
      columns.push(column);
    });

    return columns;
  };

  return (
    <$Skeleton
      loading={ratesHistory.isLoading}
      active
      paragraph={{
        rows: 2,
      }}
    >
      <>
        <div>
          <strong>{t("US.COLLECTION.ADMIN:STANDARD_RATE.HISTORY")}</strong>
        </div>
        <$TableTree
          rowKey={(record: any) => record.historyId}
          data={ratesHistory.data}
          size="small"
          filterOnType
          bordered
          className="mt-3"
          onSort={(sortData, dataSource) => {
            return sortData(dataSource);
          }}
          onFilter={(searchData, dataSource) => {
            return searchData(dataSource);
          }}
          pagination={false}
          scroll={{ x: "100%", y: "calc(((100vh - 180px) / 2) - 100px)" }}
          columns={getColumns()}
          data-testid="history-table"
          locale={{
            filterConfirm: t("COMMON.OK"),
            filterReset: t("COMMON.RESET"),
          }}
          resetOnSourceChange={true}
          virtual={true}
        />
      </>
    </$Skeleton>
  );
};

const mapStateToProps = (state: RootState) => {
  const { standardRates } = state;
  const { ratesHistory } = standardRates;

  return {
    ratesHistory,
  };
};

const connector = connect(mapStateToProps, {});

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(HistoryTable);
